import { ApiCall } from '../ApiClient';
import { BoardData, BoardLiteData, BoardParticipantData, ResultData, UnknownRangeResult, UserLiteData } from '../ApiTypes';
import { dateTransformer } from '../ApiUtil';

const get = <TResponse>(def: Omit<ApiCall<never, TResponse>, 'method'>): ApiCall<never, TResponse> =>
    Object.assign(def, { method: 'get' });

const post = <TResponse>(def: Omit<ApiCall<any, TResponse>, 'method'>): ApiCall<any, TResponse> =>
    Object.assign(def, { method: 'post' });

export type BoardThumbInfo = {
    ready: boolean,
    url: string,
    legacy?: boolean,
};

export type BoardHeadData = {
    id: string,
    accountId: string,
    type?: string,
    label?: string,
    source?: string,
    thumbUrl?: string,
};

export type BoardJoinResultData = {
    joined: boolean,
    reason?: string,
};

export type MyBoardData = BoardLiteData & { thumbUrl: string, workspaceLabel: string, workspaceType: string, lastSeenAt: Date };

export interface LegacyBoardData {
    id: string;
    label: string;
    thumbUrl: string;
    children?: string[];
    createdAt: Date;
    updatedAt: Date;
    lastSeenAt: Date;
}

export interface BoardListQuery {
    workspaceId?: string;
    mode: string;
    search: string;
    sort: string;
    offset: string;
    limit: string;
}

export interface BoardListQuery2 {
    workspaceId?: string;
    folderId?: string;
    search: string;
    sort: string;
    offset: number;
    limit: number;
}

export type CreateBoardBody = { source: string, label?: string, type?: number, workspaceId?: string, folderId?: string };
export type DeleteBoardBody = { boardId: string };
export type MoveBoardBody = { boardId: string, toId: string };
export type MoveBoardResult = { boardId: string, workspaceId: string, folderId?: string };
export type SetBoardPinnedBody = { boardId: string, workspaceId: string, value: boolean };
export type SetBoardSharingBody = { boardId: string, viewers: BoardData['viewers'], joiners: BoardData['joiners'] };

export type HoistBoardObjectsBody = {
    boardId: string,
    objects: { id: string, type: string, data: any }[],
};

export type NotifyBoardMentionsBody = {
    boardId: string,
    mentions: {
        userId: string,
        senderId: string,
        tag: string,
        objectId: string,
        objectType: string,
    }[],
};

export const boardsEndpoints = {

    head: (boardId: string) => get<BoardHeadData>({
        path: '/boards.head',
        query: { boardId },
        transform: dateTransformer(),
    }),

    info: (boardId: string, ...select: 'guests'[]) => get<BoardData>({
        path: '/boards.info',
        query: { boardId, select: (select ?? []).join(',') },
        transform: dateTransformer(),
    }),

    list: (query: BoardListQuery) => get<MyBoardData[]>({
        path: '/boards.list',
        query: { ...query },
        transform: dateTransformer(),
    }),

    list2: (query: BoardListQuery2) => get<UnknownRangeResult<MyBoardData>>({
        path: '/boards.list2',
        query: { ...query },
        transform: dateTransformer(),
    }),

    legacy: {

        list: (query: Omit<BoardListQuery, 'mode'>) => get<LegacyBoardData[]>({
            path: '/boards.legacy.list',
            query: { ...query },
            transform: dateTransformer(),
        }),
    },

    members: {

        list: (boardId: string) => get<UserLiteData[]>({
            path: '/boards.members.list',
            query: { boardId },
            transform: dateTransformer(),
        }),

        add: (boardId: string, subjectId: string) => post<ResultData>({
            path: '/boards.members.add',
            body: { boardId, subjectId },
        }),

        remove: (boardId: string, subjectId: string) => post<ResultData>({
            path: '/boards.members.remove',
            body: { boardId, subjectId },
        }),

    },

    participants: {

        info: (boardId: string, userId: string) => get<BoardParticipantData>({
            path: '/boards.participants.info',
            query: { boardId, userId },
            transform: dateTransformer(),
        }),

        list: (boardId: string) => get<BoardParticipantData[]>({
            path: '/boards.participants.list',
            query: { boardId },
            transform: dateTransformer(),
        }),

    },

    objects: {

        hoist: (body: HoistBoardObjectsBody) => post<ResultData>({
            path: '/boards.objects.hoist',
            body,
        }),

    },

    mentions: {

        notify: (body: NotifyBoardMentionsBody) => post<ResultData>({
            path: '/boards.mentions.notify',
            body,
        }),

    },

    importList: (accountId: string) => get<{ id: string, label: string, thumbUrl?: string, importedTo?: string, createdAt: Date }[]>({
        path: '/boards.importList',
        query: { accountId },
        transform: dateTransformer(),
    }),

    thumb: (boardId: string) => get<BoardThumbInfo>({
        path: '/boards.thumb',
        query: { boardId },
    }),

    create: (body: CreateBoardBody) => post<BoardData>({
        path: '/boards.create',
        body,
    }),

    delete: (body: DeleteBoardBody) => post<ResultData>({
        path: '/boards.delete',
        body,
    }),

    patch: (boardId: string, patch: Pick<BoardData, 'name'>) => post<ResultData>({
        path: '/boards.patch',
        body: { id: boardId, ...patch },
    }),

    move: (body: MoveBoardBody) => post<MoveBoardResult>({
        path: '/boards.move',
        body,
    }),

    join: (boardId: string, external: boolean) => post<BoardJoinResultData>({
        path: '/boards.join',
        body: { boardId, external },
    }),

    export: (boardId: string) => post<{ fileId: string }>({
        path: '/boards.export',
        body: { boardId },
    }),

    setPinned: (body: SetBoardPinnedBody) => post<any>({
        path: '/boards.setPinned',
        body,
    }),

    setSharing: (body: SetBoardSharingBody) => post<any>({
        path: '/boards.setSharing',
        body,
    }),

};
